import FormFactory from "@/components/modules/FormFactory/FormFactory";
import { IntegrationConfigurationFragment } from "@/apollo/types";
import { useMemo, useState } from "react";

type FormDisplayProps = {
  integration: IntegrationConfigurationFragment;
  isEditMode: boolean;
  onChangeForm: (formId: string) => void;
};

function removeNonEditableFields(
  form: IntegrationConfigurationFragment["form"],
): IntegrationConfigurationFragment["form"] {
  return {
    ...form,
    fields: form.fields
      .map((f: any) => {
        if (f.type === "sub_form") {
          return removeNonEditableFields(f);
        }

        return f;
      })
      .filter((f: any) => "editable" in f && f.editable),
  };
}

export default function FormDisplay({
  integration,
  isEditMode,
  onChangeForm,
}: FormDisplayProps) {
  const formOptions = useMemo(() => {
    const forms = integration.forms ?? [integration.form]; // Fallback to single form if no forms are defined. Should not be the case as it is mapped server-side.
    return forms.map((form: IntegrationConfigurationFragment["form"]) => {
      return isEditMode ? removeNonEditableFields(form) : form;
    });
  }, [integration, isEditMode]);

  const [selectedFormId, setSelectedFormId] = useState<string | undefined>(
    formOptions.find((f: Record<any, string>) => f.default)?.[0]?.id ??
      formOptions[0]?.id,
  );

  const selectedForm = useMemo(() => {
    if (!selectedFormId) {
      return undefined;
    }
    const selectedChoice = formOptions.find(
      (choice: IntegrationConfigurationFragment["form"]) =>
        choice.id === selectedFormId,
    );
    return selectedChoice;
  }, [formOptions, selectedFormId]);

  return (
    <>
      {formOptions.length > 1 && (
        <>
          <p className="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Authentication Method:
          </p>
          <fieldset>
            <legend className="sr-only">Configuration type</legend>
            <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
              {formOptions.map(
                (choice: IntegrationConfigurationFragment["form"]) => (
                  <div
                    key={choice.id}
                    onClick={() => {
                      setSelectedFormId(choice.id);
                      onChangeForm(choice.id);
                    }}
                    className="flex cursor-pointer items-center rounded-md border border-gray-300 p-4 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    <input
                      id={`form-choice-tab-${choice.id}`}
                      name="form-choice-tab"
                      type="radio"
                      value={choice.id}
                      checked={selectedFormId === choice.id}
                      onChange={() => {
                        setSelectedFormId(choice.id);
                        onChangeForm(choice.id);
                      }}
                      className="text-primary-600 focus:ring-primary-500 dark:text-primary-500 dark:focus:ring-primary-600 h-4 w-4 cursor-pointer border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <label
                      htmlFor={`form-choice-tab-${choice.id}`}
                      className="ml-3 block cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-100"
                    >
                      {choice.formName}
                    </label>
                  </div>
                ),
              )}
            </div>
          </fieldset>
        </>
      )}
      {selectedForm && (
        <div className="mt-6">
          <FormFactory form={selectedForm} />
        </div>
      )}
    </>
  );
}
